import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { PayeeCleanupButton } from "@/components/PayeeCleanupDialog";

const YN = ({ v }) => <span className={v ? "text-emerald-700" : "text-rose-600"}>{v ? "Yes" : "No"}</span>;

const RecordRow = ({ r }) => (
  <tr className={`border-t ${r.is_most_complete ? "bg-emerald-50/60" : ""}`} data-testid={`dup-record-${r.id}`}>
    <td className="p-2 font-mono text-[11px]">{r.id.slice(0, 8)}{r.is_most_complete && <Badge className="ml-1 bg-emerald-600 text-[9px]">Most complete</Badge>}</td>
    <td className="p-2">{r.name}<div className="text-[10px] text-slate-500">{r.payee_role} · {r.status}</div></td>
    <td className="p-2">{r.linked_user_name || "—"}<div className="text-[10px] text-slate-500">{r.employee_code || ""} {r.linked_user_email || ""}</div></td>
    <td className="p-2"><YN v={r.pan_filled} /></td>
    <td className="p-2"><YN v={r.bank_filled} /></td>
    <td className="p-2 font-mono">{r.account_last4 ? `••${r.account_last4}` : "—"}</td>
    <td className="p-2"><YN v={r.ifsc_filled} /></td>
    <td className="p-2">{(r.created_at || "").slice(0, 10)}<div className="text-[10px] text-slate-500">{r.created_by_name}</div></td>
    <td className="p-2 text-[11px]">R {r.refs.reimbursements} · E {r.refs.expenses} · A {r.refs.advances} · Teams {r.refs.active_project_teams}</td>
  </tr>
);

const Group = ({ g }) => (
  <Card data-testid="dup-group">
    <CardContent className="p-3">
      <div className="text-sm font-semibold mb-2">{g.key} <Badge variant="outline" className="ml-1">{g.count} records</Badge> <Badge variant="secondary" className="ml-1">{g.match_type === "name" ? "Same name" : "Same linked user"}</Badge></div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-left text-slate-500"><tr>{["Payee ID", "Name / type", "Linked user", "PAN", "Bank", "A/c", "IFSC", "Created", "Used in"].map((h) => <th key={h} className="p-2 font-medium">{h}</th>)}</tr></thead>
          <tbody>{g.records.map((r) => <RecordRow key={r.id} r={r} />)}</tbody>
        </table>
      </div>
    </CardContent>
  </Card>
);

export const PayeeDuplicatesReport = () => {
  const [data, setData] = useState(null);
  const load = async () => {
    setData(null);
    const r = await api.get("/v2/payees/duplicates-report");
    setData(r.data);
  };
  useEffect(() => { load(); }, []);
  if (!data) return <div className="text-sm text-slate-500" data-testid="dup-report-loading">Loading report…</div>;
  return (
    <div className="space-y-3" data-testid="dup-report">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600" data-testid="dup-report-summary">
          Read-only. {data.total_payees} payees · {data.duplicate_groups} duplicate groups · {data.extra_records} extra records.
        </div>
        <div className="flex gap-2">
          <PayeeCleanupButton onDone={load} />
          <Button size="sm" variant="outline" onClick={load} data-testid="dup-report-refresh"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
        </div>
      </div>
      {data.groups.length === 0 && <div className="text-sm text-emerald-700" data-testid="dup-report-empty">No duplicate payees found.</div>}
      {data.groups.map((g) => <Group key={`${g.match_type}-${g.key}`} g={g} />)}
    </div>
  );
};
